import { PageHeader } from "@organizacaox/lai-design-system";
// Blocos visuais do Painel. O `dashboard.tsx` ficou só com a orquestração de
// dados (queries, gates de papel, período) e importa daqui tudo que desenha.
//
// COR: o Disk é white-label. `applyBrandColor` (lib/brand-theme.ts) reescreve
// --brand-hue/chroma/l a partir da cor da marca do cliente, e todo o resto é
// derivado. Por isso nada aqui tem número de cor: as superfícies tingidas saem
// de `color-mix`/`oklch(from ...)` sobre var(--primary). Um hex fixo aqui
// deixaria azul o app de um cliente laranja.
import type { CSSProperties, ReactNode } from "react";
import type { LucideIcon } from "@organizacaox/lai-design-system/icons";
import { useTranslation } from "react-i18next";

import { Avatar, AvatarFallback } from "@organizacaox/lai-design-system";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@organizacaox/lai-design-system";
import { Tooltip, TooltipContent, TooltipTrigger } from "@organizacaox/lai-design-system";
import { Link } from "@organizacaox/lai-design-system/router";
import { ArrowRight } from "@organizacaox/lai-design-system/icons";
import { cn } from "@/lib/utils";
import { activeLocale } from "./lib/locale-format.js";
import type { DialerAgentAvailability } from "./api.js";
import { avatarHue, presenceOrder, type TeamPresence } from "./dashboard-model.js";
import { CountUp, useIntroActive } from "./dashboard-intro.js";

// Quantos avatares o cluster mostra antes de virar "+N". Cinco é o que cabe sem
// encostar na contagem em telas estreitas.
const CLUSTER_FACES = 5;

// "Ana Maria Souza" → "AS". Duas letras no máximo: três já não cabem no círculo.
function monogram(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]![0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]![0]! : "";
  return (first + last).toUpperCase();
}

// "Quinta-feira, 17 de setembro" no idioma ativo.
function longDate(now: Date): string {
  return new Intl.DateTimeFormat(activeLocale(), {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(now);
}

/* -------------------------------------------------------------------------- */
/* Entrada                                                                     */
/* -------------------------------------------------------------------------- */

// Entrada da tela: um único movimento orquestrado, de cima para baixo, e só na
// primeira dobra. Abaixo dela não há animação — repetir o efeito em cada cartão
// da página vira ruído, não hierarquia.
//
// Tudo sob `motion-safe:`: quem pediu menos movimento no sistema vê a tela já
// montada, sem transição.
export const REVEAL = "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200";

// Degrau entre um bloco e o seguinte. Curto de propósito: a tela tem que
// parecer que assentou, não que está sendo montada peça por peça.
export function revealDelay(step: number): { animationDelay: string } {
  return { animationDelay: `${Math.min(step, 2) * 40}ms` };
}

/* -------------------------------------------------------------------------- */
/* Cabeçalho                                                                   */
/* -------------------------------------------------------------------------- */

// Cluster de avatares da equipe com a contagem de presença ao lado. Os pontos
// coloridos repetem a informação que o texto já dá — ficam como reforço rápido,
// nunca como único portador (daí o texto vir junto, não em tooltip).
function PresenceCluster({
  agents,
  presence,
}: {
  agents: DialerAgentAvailability[];
  presence: TeamPresence;
}) {
  const { t } = useTranslation();
  const ordered = presenceOrder(agents);
  const faces = ordered.slice(0, CLUSTER_FACES);
  const overflow = ordered.length - faces.length;

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-card/60 px-4 py-2.5 shadow-surface backdrop-blur">
      <div className="flex -space-x-2">
        {faces.map((agent) => (
          // O monograma esconde o nome; o tooltip devolve. `render` monta o
          // gatilho SOBRE o próprio avatar, sem um wrapper que quebraria o
          // encaixe do `-space-x-2`.
          <Tooltip key={agent.userId}>
            <TooltipTrigger render={<Avatar className="size-8 shrink-0 ring-2 ring-card" />}>
              {/* Cada pessoa com a SUA matiz: num cluster de monogramas, a cor é
                o que separa uma cara da outra à distância. Luminosidade e croma
                são fixos — só a matiz vem do nome, para nenhuma cair fora da
                faixa que sustenta texto branco. */}
              <AvatarFallback
                className="text-[0.7rem] font-semibold"
                style={{
                  backgroundColor: `oklch(0.58 0.15 ${avatarHue(agent.name)})`,
                  color: "oklch(0.99 0 0)",
                }}
              >
                {monogram(agent.name)}
              </AvatarFallback>
            </TooltipTrigger>
            <TooltipContent>{agent.name}</TooltipContent>
          </Tooltip>
        ))}
        {overflow > 0 && (
          <span className="flex size-8 items-center justify-center rounded-full bg-muted text-[0.7rem] font-semibold text-muted-foreground ring-2 ring-card">
            +{overflow}
          </span>
        )}
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold tabular-nums">
          {t("dashboard.presence.people", { count: presence.total })}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("dashboard.presence.breakdown", {
            inCall: presence.inCall,
            available: presence.available,
            offDuty: presence.offDuty,
          })}
        </p>
      </div>
    </div>
  );
}

export function DashboardGreeting({
  greeting,
  firstName,
  now,
  agents,
  presence,
  className,
  style,
}: {
  greeting: string;
  firstName: string;
  now: Date;
  agents: DialerAgentAvailability[];
  presence: TeamPresence;
  className?: string;
  style?: CSSProperties;
}) {
  const { t } = useTranslation();
  return (
    <div className={className} style={style}>
      <PageHeader
        title={`${greeting}, ${firstName}`}
        description={t("dashboard.greeting.subtitle", { date: longDate(now) })}
        actions={
          agents.length > 0 ? <PresenceCluster agents={agents} presence={presence} /> : undefined
        }
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Cartão-herói                                                                */
/* -------------------------------------------------------------------------- */

// KPI dentro do herói: vive sobre a superfície escura da marca, então toda a
// tinta é --hero-ink em opacidades diferentes — nunca uma cor própria, que
// brigaria com a marca do cliente.
export function HeroKpi({
  icon: Icon,
  value,
  label,
  hint,
}: {
  icon: LucideIcon;
  value: string;
  label: string;
  hint: string;
}) {
  return (
    <div className="min-w-0 flex-1 px-4 py-3 first:pl-0">
      <span className="flex size-8 items-center justify-center rounded-lg bg-[color-mix(in_oklab,var(--hero-ink)_16%,transparent)]">
        <Icon aria-hidden="true" className="size-4 opacity-90" />
      </span>
      <p className="mt-2.5 text-2xl font-semibold tabular-nums tracking-tight">
        <MetricValue value={value} />
      </p>
      <p className="text-xs font-medium opacity-90">{label}</p>
      <p className="truncate text-xs opacity-65">{hint}</p>
    </div>
  );
}

// O único lugar exagerado da tela: superfície tingida da marca, número em
// escala de display e a curva do dia sangrando no fundo. Tudo em volta é
// contido de propósito — se dois blocos gritam, nenhum é lido primeiro.
export function HeroCard({
  eyebrow,
  value,
  unit,
  trend,
  sparkline,
  className,
  style,
  children,
  "aria-label": ariaLabel,
}: {
  eyebrow: string;
  value: string;
  unit: string;
  trend?: ReactNode;
  sparkline?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  // Nome da região para leitor de tela; sem ele a <section> não vira landmark.
  "aria-label"?: string;
}) {
  return (
    <section
      aria-label={ariaLabel}
      className={cn(
        "relative isolate overflow-hidden rounded-3xl p-6 text-[var(--hero-ink)] shadow-overlay sm:p-7",
        className,
      )}
      // A superfície é a matiz e o croma da MARCA com a luminosidade CRAVADA
      // (`oklch(from ...)` troca só o L). Sem isso o herói dependeria de onde a
      // cor do cliente cai na escala: a marca roxa do padrão dá um fundo claro
      // com texto quase-preto por cima, e uma marca amarela daria um fundo que
      // não sustenta texto nenhum. Fixando L em 0.26–0.38 a superfície é sempre
      // escura, a tinta é sempre clara, e o contraste passa em qualquer marca.
      //
      // `background-color` vem numa declaração separada de propósito: é o
      // fallback se o navegador não entender relative color syntax — perde-se o
      // gradiente, não a legibilidade.
      style={{
        ...style,
        ["--hero-ink" as string]: "oklch(from var(--primary) 0.97 0.02 h)",
        backgroundColor: "color-mix(in oklab, var(--primary) 45%, black)",
        backgroundImage:
          "radial-gradient(120% 130% at 6% -25%, oklch(from var(--primary) 0.52 c h) 0%, transparent 58%), linear-gradient(155deg, oklch(from var(--primary) 0.36 c h) 0%, oklch(from var(--primary) 0.24 c h) 100%)",
      }}
    >
      {/* Faixa baixa, atrás de tudo: a curva dá o formato do dia como textura.
          Subir mais do que isso a transformaria em gráfico — e gráfico com
          número gigante por cima não se lê nem como um nem como outro. */}
      {sparkline && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-24 opacity-45"
        >
          {sparkline}
        </div>
      )}

      <p className="text-sm font-medium opacity-75">{eyebrow}</p>
      <div className="mt-1 flex flex-wrap items-end gap-x-3 gap-y-1">
        <span className="font-heading text-6xl font-semibold tabular-nums tracking-tighter sm:text-7xl">
          <MetricValue value={value} />
        </span>
        <span className="pb-2 text-sm opacity-75">{unit}</span>
        {trend}
      </div>
      {children}
    </section>
  );
}

// Variação vs. ontem, dentro do herói. Não usa verde/âmbar como no resto do app:
// sobre a superfície da marca as duas cores ficariam ilegíveis em metade das
// paletas possíveis. A direção vem da seta e do sinal, que não dependem de cor.
export function HeroTrend({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <span className="mb-2 flex items-center gap-1.5 rounded-full bg-[color-mix(in_oklab,var(--hero-ink)_14%,transparent)] px-2.5 py-1 text-xs font-medium tabular-nums">
      <Icon aria-hidden="true" className="size-3.5" />
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Cartões de seção                                                            */
/* -------------------------------------------------------------------------- */

// Cabeçalho comum dos cartões: ícone em círculo tingido, título, e um subtítulo
// que diz o que o cartão responde. O link para a tela que aprofunda o assunto é
// o que impede o painel de virar relatório.
export function SectionCard({
  icon: Icon,
  title,
  description,
  to,
  linkLabel,
  action,
  className,
  contentClassName,
  style,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  to?: string;
  linkLabel?: string;
  // Ação livre no canto do cabeçalho, para quando o atalho precisa de mais que
  // um caminho fixo (ex.: levar o período junto na busca).
  action?: ReactNode;
  className?: string;
  contentClassName?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <Card className={cn("dashboard-live-card rounded-2xl", className)} style={style}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon aria-hidden="true" className="size-4.5" />
          </span>
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold tracking-tight">{title}</CardTitle>
            {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
          </div>
        </div>
        {to && linkLabel && (
          <Link
            to={to}
            className="dashboard-detail-link flex shrink-0 items-center gap-1 rounded-sm text-xs text-muted-foreground hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
          >
            {linkLabel}
            <ArrowRight aria-hidden="true" className="size-3" />
          </Link>
        )}
        {action && <div className="shrink-0">{action}</div>}
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}

// Cartão de métrica dos relatórios, ao lado do herói: número grande, ícone
// tingido e, quando o número é parte de um todo, a barra da proporção.
// Tom semântico do card: `danger` para perda (vermelho), `warning` para o que
// não é culpa do lead (âmbar). Sem tom, fica na cor da marca.
const METRIC_TONES = {
  default: { icon: "bg-primary/10 text-primary", bar: "bg-primary" },
  danger: { icon: "bg-destructive/10 text-destructive", bar: "bg-destructive" },
  warning: {
    icon: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    bar: "bg-amber-500",
  },
} as const;

// `onClick` transforma o card num botão de filtro (aria-pressed = `active`).
export function MetricCard({
  title,
  value,
  hint,
  icon: Icon,
  share,
  tone = "default",
  onClick,
  active = false,
  actionLabel,
}: {
  title: string;
  value: string;
  hint?: string;
  icon: LucideIcon;
  share?: number | null;
  tone?: keyof typeof METRIC_TONES;
  onClick?: () => void;
  active?: boolean;
  /** Nome acessível do botão quando `onClick` está ligado. */
  actionLabel?: string;
}) {
  const colors = METRIC_TONES[tone];
  const card = (
    <Card
      className={cn(
        "min-w-0 rounded-2xl",
        onClick && "h-full transition-colors group-hover:border-foreground/20",
        active && "border-foreground/40 ring-1 ring-foreground/20",
      )}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div className="min-w-0 space-y-2">
          <CardDescription>{title}</CardDescription>
          <CardTitle className="text-3xl font-semibold tracking-tight tabular-nums">
            <MetricValue value={value} />
          </CardTitle>
        </div>
        <span
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-full",
            colors.icon,
          )}
        >
          <Icon aria-hidden="true" className="size-4" />
        </span>
      </CardHeader>
      <CardContent className="mt-auto space-y-3">
        {hint && <p className="text-xs leading-relaxed text-muted-foreground">{hint}</p>}
        {share != null && (
          <div aria-hidden="true" className="h-1 overflow-hidden rounded-full bg-muted">
            <div
              className={cn("dashboard-status-bar h-full rounded-full", colors.bar)}
              style={{ width: `${Math.min(100, Math.max(0, share))}%` }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
  if (!onClick) return card;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={actionLabel}
      className="group min-w-0 cursor-pointer rounded-2xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {card}
    </button>
  );
}

// Tile dos indicadores do período. A micro-barra embaixo só aparece quando o
// número TEM denominador (uma taxa) — numa contagem absoluta ela não
// significaria nada, e uma barra sem significado é decoração.
export function KpiTile({
  icon: Icon,
  label,
  value,
  hint,
  share,
  tone = "text-foreground",
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint: string;
  share?: number | null;
  tone?: string;
}) {
  return (
    <Card className="rounded-2xl">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon aria-hidden="true" className="size-4" />
          <span className="truncate text-xs font-medium">{label}</span>
        </div>
        <p className={cn("mt-2 text-3xl font-semibold tabular-nums tracking-tight", tone)}>
          <MetricValue value={value} />
        </p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{hint}</p>
        {share != null && (
          <div className={cn("mt-3 h-1 overflow-hidden rounded-full bg-muted", tone)}>
            <div
              className="dashboard-status-bar h-full rounded-full bg-current opacity-70"
              style={{ width: `${Math.min(100, Math.max(0, share))}%` }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Na abertura do Painel (ver dashboard-intro.tsx) o número conta a partir do
// zero; fora dela, só o realce curto de quando o valor muda.
export function MetricValue({ value }: { value: string | number }) {
  return <MetricValueInner key={value} value={String(value)} />;
}

function MetricValueInner({ value }: { value: string }) {
  const intro = useIntroActive();
  if (intro) return <CountUp value={value} className="dashboard-metric-value" />;
  return <span className="dashboard-metric-value">{value}</span>;
}
